/**
 * Admin API client for re-running module analysis with a chosen provider.
 *
 * @module adminReanalysisApi
 */

import { apiPost } from '@/lib/apiUtils';

export type ReanalysisProvider = 'openai' | 'anthropic' | 'gemini';

export interface ReanalysisError {
  readonly questionId?: string | null;
  readonly answerId?: string | null;
  readonly error: string;
  readonly section?: number | null;
}

export interface ReanalysisPayload {
  readonly module: string;
  readonly level: string | null;
  readonly provider: string;
  readonly analysisModel?: string | null;
  readonly answerCount: number;
  readonly successCount: number;
  readonly grades: Record<string, number>;
  readonly analysis_breakdowns: Record<string, Record<string, unknown>>;
  readonly transcriptions: Record<string, string>;
  readonly diagnostic_tags: Record<string, readonly string[]>;
  readonly errors: readonly ReanalysisError[];
}

export interface ReanalysisCommitResponse {
  readonly successCount: number;
  readonly module: string;
}

export async function previewModuleReanalysis(
  sessionId: string,
  module: string,
  provider: ReanalysisProvider,
): Promise<ReanalysisPayload | null> {
  return apiPost<ReanalysisPayload>(
    `/admin/submissions/${encodeURIComponent(sessionId)}/module/${encodeURIComponent(module)}/reanalyse-preview`,
    { provider },
    {
      context: 'previewModuleReanalysis',
      errorMessage: 'Failed to run re-analysis preview',
      errorReturnValue: null,
    },
  );
}

export async function commitModuleReanalysis(
  sessionId: string,
  module: string,
  payload: ReanalysisPayload,
): Promise<ReanalysisCommitResponse | null> {
  return apiPost<ReanalysisCommitResponse>(
    `/admin/submissions/${encodeURIComponent(sessionId)}/module/${encodeURIComponent(module)}/reanalyse-commit`,
    payload,
    {
      context: 'commitModuleReanalysis',
      errorMessage: 'Failed to save re-analysis',
      errorReturnValue: null,
    },
  );
}
